import { InformationCircleIcon } from "@heroicons/react/24/outline"
import React, { useMemo, useState } from "react"
import { useMutation } from "urql"

import Breadcrumbs from "../../components/shared/Breadcrumbs"
import { Button } from "../../components/shared/Buttons"
import HealMeTooltip from "../../components/shared/HealMeTooltip"
import { Label, Select, TextArea, Toggle } from "../../components/shared/Inputs"
import { Banner, Box, Divider } from "../../components/shared/Layout"
import { UnderlinedLink } from "../../components/shared/Links"
import { RadioWithLabel } from "../../components/shared/RadioButtons"
import Typography from "../../components/shared/Typography"
import SwitchCollapsableContent from "../../components/SwitchCollapsableContent"
import { useToast, ToastProvider } from "../../contexts/ToastContext"
import GraphQLProvider from "../../providers/GraphQLProvider"
import ServiceSelector from "../DiscountCodes/ServiceSelector"

const UPDATE_BOOKING_SETTINGS = `
  mutation UpdateBookingSettings(
    $appointmentBufferTime: Int,
    $instantClientApproval: Boolean,
    $appointmentLeadTime: Int,
    $startTimeIncrement: Int,
    $requireClientCcToBook: Boolean,
    $chargeClientCcAtBooking: Boolean,
    $manualChargeOnly: Boolean,
    $cancellationPolicy: String,
    $sendReviewRequestAfterPaidSession: Boolean,
    $sendReviewRequestAfterFreeConsult: Boolean,
    $autoSendIntakeForNewOnlineClients: Boolean,
    $autoIntakeServiceScope: String,
    $autoIntakeServiceIds: [ID!],
    $autoIntakeFormScope: String,
    $autoIntakeIntakeFormIds: [ID!],
  ) {
    updateBookingSettings(
      appointmentBufferTime: $appointmentBufferTime,
      instantClientApproval: $instantClientApproval,
      appointmentLeadTime: $appointmentLeadTime,
      startTimeIncrement: $startTimeIncrement,
      requireClientCcToBook: $requireClientCcToBook,
      chargeClientCcAtBooking: $chargeClientCcAtBooking,
      manualChargeOnly: $manualChargeOnly,
      cancellationPolicy: $cancellationPolicy,
      sendReviewRequestAfterPaidSession: $sendReviewRequestAfterPaidSession,
      sendReviewRequestAfterFreeConsult: $sendReviewRequestAfterFreeConsult,
      autoSendIntakeForNewOnlineClients: $autoSendIntakeForNewOnlineClients,
      autoIntakeServiceScope: $autoIntakeServiceScope,
      autoIntakeServiceIds: $autoIntakeServiceIds,
      autoIntakeFormScope: $autoIntakeFormScope,
      autoIntakeIntakeFormIds: $autoIntakeIntakeFormIds,
    ) {
      result
      error
    }
  }
`

const mergeBookingDefaults = (raw, multipleIntakeFormsEnabled = true) => {
  let formScope = raw.autoIntakeFormScope ?? "all_active"
  if (!multipleIntakeFormsEnabled && formScope === "specific") {
    formScope = "all_active"
  }
  return {
    ...raw,
    sendReviewRequestAfterPaidSession: raw.sendReviewRequestAfterPaidSession ?? true,
    sendReviewRequestAfterFreeConsult: raw.sendReviewRequestAfterFreeConsult ?? false,
    autoSendIntakeForNewOnlineClients: raw.autoSendIntakeForNewOnlineClients ?? false,
    autoIntakeServiceScope: raw.autoIntakeServiceScope ?? "all",
    autoIntakeServiceIds: raw.autoIntakeServiceIds ?? [],
    autoIntakeFormScope: formScope,
    autoIntakeIntakeFormIds: raw.autoIntakeIntakeFormIds ?? []
  }
}

const options = [
  { value: true, label: "Yes (recommended)" },
  { value: false, label: "No (approval required)" }
]

const bufferIntervals = [
  { value: 0, label: "None" },
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes (recommended)" },
  { value: 20, label: "20 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1.5 hours" },
  { value: 120, label: "2 hours" },
  { value: 150, label: "2.5 hours" },
  { value: 180, label: "3 hours" }
]

const leadTimes = [
  { value: 0, label: "No lead time" },
  { value: 15, label: "15 minutes in advance" },
  { value: 30, label: "30 minutes in advance" },
  { value: 60, label: "1 hour in advance" },
  { value: 180, label: "3 hours in advance" },
  { value: 480, label: "8 hours in advance" },
  { value: 720, label: "12 hours in advance" },
  { value: 1440, label: "1 day in advance (recommended)" },
  { value: 2880, label: "2 days in advance" },
  { value: 4320, label: "3 days in advance" },
  { value: 10080, label: "1 week in advance" }
]

const startTimeIncrements = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes (recommended)" },
  { value: 60, label: "60 minutes" },
  { value: 75, label: "75 minutes" },
  { value: 90, label: "1.5 hours" },
  { value: 120, label: "2 hours" },
  { value: 150, label: "2.5 hours" },
  { value: 180, label: "3 hours" },
  { value: 240, label: "4 hours" },
  { value: 300, label: "5 hours" },
  { value: 360, label: "6 hours" }
]

const BookingSettings = ({
  autoSendIntakeFormsBookingSettingsEnabled,
  automatedReviewRequestSettingsEnabled,
  bookingSettings,
  activeIntakeForms = [],
  bookableServices = [],
  cancellationPolicyEnabled,
  hasOffline,
  hasPaymentProvider,
  intakeFormsEnabled,
  manualChargeEnabled,
  multipleIntakeFormsEnabled,
  requireCcEnabled
}) => {
  const [, updateBookingSettings] = useMutation(UPDATE_BOOKING_SETTINGS)

  const initial = useMemo(
    () => mergeBookingDefaults(bookingSettings, multipleIntakeFormsEnabled),
    [bookingSettings, multipleIntakeFormsEnabled]
  )
  const [settings, setSettings] = useState(initial)
  const [originalSettings, setOriginalSettings] = useState(initial)

  const intakeFormOptions = useMemo(
    () => activeIntakeForms.map((f) => ({ id: f.id, name: f.name })),
    [activeIntakeForms]
  )

  const { showToast } = useToast()

  const handleSave = () => {
    updateBookingSettings(settings)
      .then((result) => {
        const payload = result.data?.updateBookingSettings
        if (payload?.result === "success") {
          showToast({ content: "Settings updated successfully.", timeout: 3000 })
          setOriginalSettings(mergeBookingDefaults(settings, multipleIntakeFormsEnabled))
        } else {
          const gqlErrors = payload?.error
          const error =
            result.error?.message?.replace("[GraphQL]", "") ||
            (Array.isArray(gqlErrors) ? gqlErrors.join(", ") : gqlErrors) ||
            "Please try again later."

          showToast({ type: "error", content: error })
        }
      })
      .catch((error) => {
        showToast({ type: "error", content: error.message })
      })
  }

  if (Object.keys(settings).length === 0) {
    return null
  }

  const settingsChanged = JSON.stringify(settings) !== JSON.stringify(originalSettings)

  return (
    <div>
      <Breadcrumbs links={[{ href: "/portal/settings", text: "Settings" }]} activePage="Booking" />
      <div className="mb-4 flex flex-row justify-between">
        <div className="flex flex-col">
          <Typography as="h2" variant="h3">
            Booking
          </Typography>
          <Typography variant="subtitle" className="sm:hidden">
            Auto-accept new bookings, edit the time between appointments, and more.
          </Typography>
        </div>

        <div className="mr-2">
          <Button
            className="mr-4"
            type="tertiary"
            onClick={() => {
              setSettings(mergeBookingDefaults(originalSettings, multipleIntakeFormsEnabled))
              showToast({ content: "Your changes have been discarded.", timeout: 3000 })
            }}>
            Cancel
          </Button>
          <Button type="primary" disabled={!settingsChanged} onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
      <Box className="w-1/2 sm:w-full">
        {requireCcEnabled && (
          <div className="mb-6">
            <div className="flex flex-col">
              <Label htmlFor="require-booking" className="mb-2 text-base">
                Require credit card when a client books:
              </Label>
              <Typography variant="subtitle" className="mb-4">
                Booking free services will not require a credit card. You can still book from the portal without a card
                on file; this setting only blocks client self-serve booking.
              </Typography>

              {hasPaymentProvider ? (
                <div>
                  <div className="mb-4 flex flex-row gap-4">
                    <RadioWithLabel
                      id="require-cc-yes"
                      value={true}
                      name="require-cc"
                      label="Yes"
                      checked={settings.requireClientCcToBook}
                      onChange={() => setSettings({ ...settings, requireClientCcToBook: true })}
                    />
                    <RadioWithLabel
                      value={false}
                      id="require-cc-no"
                      name="require-cc"
                      label="No (recommended)"
                      checked={!settings.requireClientCcToBook}
                      onChange={() =>
                        setSettings({ ...settings, requireClientCcToBook: false, chargeClientCcAtBooking: false })
                      }
                    />
                  </div>
                  {hasOffline && (
                    <Typography variant="subtitle" className="mb-2">
                      Your clients will not have the option to pay with another method.
                    </Typography>
                  )}
                </div>
              ) : (
                <Typography variant="subtitle" className="mb-4 text-red">
                  Requires a connected Payment Provider account.{" "}
                  <UnderlinedLink className="ml-2" href="/portal/settings/banking">
                    Connect Now
                  </UnderlinedLink>
                </Typography>
              )}
            </div>
          </div>
        )}
        <Divider />

        {requireCcEnabled && settings.requireClientCcToBook && (
          <div className="mb-6">
            <div className="flex flex-col">
              <div className="mb-2 flex flex-row items-center gap-1.5">
                <Label htmlFor="charge-on-booking" className="mb-0 text-base">
                  Charge credit card when client books:
                </Label>
                <InformationCircleIcon
                  className="size-5 shrink-0 cursor-help text-gray-dark"
                  data-tooltip-id="charge-on-booking-heading-tooltip"
                />
                <HealMeTooltip
                  id="charge-on-booking-heading-tooltip"
                  place="right"
                  content="Choose when the client's card is charged. These options only apply while a card is required to book."
                />
              </div>
              <div className="flex flex-row flex-wrap gap-4">
                <RadioWithLabel
                  id="charge-cc-yes"
                  value={true}
                  name="charge-cc"
                  label="Yes"
                  disabled={settings.manualChargeOnly}
                  disabledTooltipMessage={'Turn off "Don\'t charge automatically" to charge at booking.'}
                  tooltipMessage="Collect payment when the client books online. The appointment amount is charged right away."
                  checked={settings.chargeClientCcAtBooking && !settings.manualChargeOnly}
                  onChange={() => setSettings({ ...settings, chargeClientCcAtBooking: true, manualChargeOnly: false })}
                />
                <RadioWithLabel
                  id="charge-cc-no"
                  value={false}
                  name="charge-cc"
                  label="No (recommended)"
                  disabled={settings.manualChargeOnly}
                  disabledTooltipMessage={'Turn off "Don\'t charge automatically" to choose charge timing.'}
                  tooltipMessage="Save the card at booking without charging. Collect payment later from the appointment or the client's billing page."
                  checked={!settings.chargeClientCcAtBooking || settings.manualChargeOnly}
                  onChange={() => setSettings({ ...settings, chargeClientCcAtBooking: false })}
                />
              </div>
            </div>
            {manualChargeEnabled && (
              <div className="mt-4">
                <div className="flex flex-row items-center gap-1.5">
                  <Toggle
                    checked={!!settings.manualChargeOnly}
                    onChange={(checked) =>
                      setSettings({
                        ...settings,
                        manualChargeOnly: checked,
                        chargeClientCcAtBooking: checked ? false : settings.chargeClientCcAtBooking
                      })
                    }
                  />
                  <Label className="mb-0 ml-1 text-base">
                    Don&apos;t charge automatically (I&apos;ll charge manually)
                  </Label>
                  <InformationCircleIcon
                    className="size-5 shrink-0 cursor-help text-gray-dark"
                    data-tooltip-id="manual-charge-only-tooltip"
                  />
                  <HealMeTooltip
                    id="manual-charge-only-tooltip"
                    place="right"
                    content="Never charge automatically—not at booking and not after the session. The card stays on file; charge it yourself from an appointment or the client page when you're ready."
                  />
                </div>
                <Typography variant="subtitle" className="mt-2">
                  Clients still need a card on file to book. You can charge it from the appointment or the client page
                  whenever you&apos;re ready.
                </Typography>
              </div>
            )}
            <Divider />
          </div>
        )}
        <div className="mb-6">
          <Label htmlFor="instant-approval" className="mb-2 text-base">
            Auto-accept client appointments:
          </Label>
          <Select
            id="instant-approval"
            className="w-fit"
            options={options}
            value={settings.instantClientApproval}
            onChange={(e) => setSettings({ ...settings, instantClientApproval: e.target.value === "true" })}
            defaultOption="Select an option"
          />
        </div>
        {autoSendIntakeFormsBookingSettingsEnabled &&
          (intakeFormsEnabled ? (
            <>
              <Divider />
              <div className="mb-6">
                <Typography as="h3" variant="h5" className="mb-1">
                  Intake forms for new clients
                </Typography>
                <Typography variant="subtitle" className="mb-4">
                  Automatically send intake form invitations when a client books their{" "}
                  <strong>first appointment online</strong>, or when you book that first appointment from the calendar.
                </Typography>
                {activeIntakeForms.length === 0 ? (
                  <Banner type="warning" className="mb-4">
                    Add an active intake form before you can turn on automatic sending.{" "}
                    <UnderlinedLink className="ml-1" href="/portal/settings/intake_forms">
                      Intake forms
                    </UnderlinedLink>
                  </Banner>
                ) : null}
                <SwitchCollapsableContent
                  checked={settings.autoSendIntakeForNewOnlineClients}
                  onChange={() =>
                    setSettings((prev) => ({
                      ...prev,
                      autoSendIntakeForNewOnlineClients: !prev.autoSendIntakeForNewOnlineClients,
                      autoIntakeServiceScope: prev.autoIntakeServiceScope || "all",
                      autoIntakeFormScope: prev.autoIntakeFormScope || "all_active"
                    }))
                  }
                  label="Send intake forms automatically">
                  <div className="space-y-6 px-7 pb-6">
                    <Typography variant="subtitle">
                      Applies to a client&apos;s <strong>first</strong> appointment via online checkout or when you book
                      them from the portal. Uses the same duplicate-send protection as manual sends (for example, the
                      10-minute guard).
                    </Typography>
                    <div>
                      <Label className="mb-3 text-base">Apply to</Label>
                      <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:gap-6">
                        <RadioWithLabel
                          id="auto-intake-svc-all"
                          name="auto-intake-svc"
                          label="All services"
                          checked={settings.autoIntakeServiceScope === "all"}
                          onChange={() => setSettings({ ...settings, autoIntakeServiceScope: "all" })}
                        />
                        <RadioWithLabel
                          id="auto-intake-svc-specific"
                          name="auto-intake-svc"
                          label="Only specific services"
                          checked={settings.autoIntakeServiceScope === "specific"}
                          onChange={() =>
                            setSettings({
                              ...settings,
                              autoIntakeServiceScope: "specific"
                            })
                          }
                        />
                      </div>
                      <Typography variant="subtitle" className="mb-2">
                        Selected services qualify when they&apos;re part of the booked appointment.
                      </Typography>
                      {settings.autoIntakeServiceScope === "specific" && (
                        <ServiceSelector
                          className="max-w-xl"
                          services={bookableServices}
                          selectedServiceIds={settings.autoIntakeServiceIds}
                          onChange={(ids) => setSettings({ ...settings, autoIntakeServiceIds: ids })}
                        />
                      )}
                    </div>
                    <div>
                      <Label className="mb-3 text-base">Intake forms</Label>
                      <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:gap-6">
                        <RadioWithLabel
                          id="auto-intake-form-all"
                          name="auto-intake-form"
                          label="All active intake forms"
                          checked={settings.autoIntakeFormScope === "all_active"}
                          onChange={() => setSettings({ ...settings, autoIntakeFormScope: "all_active" })}
                        />
                        <RadioWithLabel
                          id="auto-intake-form-specific"
                          name="auto-intake-form"
                          label="Only specific forms"
                          checked={settings.autoIntakeFormScope === "specific"}
                          disabled={!multipleIntakeFormsEnabled}
                          onChange={() =>
                            multipleIntakeFormsEnabled
                              ? setSettings({ ...settings, autoIntakeFormScope: "specific" })
                              : undefined
                          }
                        />
                      </div>
                      {!multipleIntakeFormsEnabled ? (
                        <Typography variant="subtitle" className="mb-2">
                          Choosing individual forms requires multiple intake forms on your plan.{" "}
                          <UnderlinedLink href="/portal/settings/intake_forms">Manage intake forms</UnderlinedLink>
                        </Typography>
                      ) : null}
                      {settings.autoIntakeFormScope === "specific" && multipleIntakeFormsEnabled ? (
                        <ServiceSelector
                          className="max-w-xl"
                          services={intakeFormOptions}
                          selectedServiceIds={settings.autoIntakeIntakeFormIds}
                          onChange={(ids) => setSettings({ ...settings, autoIntakeIntakeFormIds: ids })}
                        />
                      ) : null}
                    </div>
                  </div>
                </SwitchCollapsableContent>
              </div>
            </>
          ) : (
            <>
              <Divider />
              <div className="mb-6">
                <Typography variant="subtitle" className="mb-2 text-gray-dark">
                  Intake form automation is available when intake forms are enabled for your account.
                </Typography>
                <UnderlinedLink href="/portal/settings/intake_forms">Intake forms settings</UnderlinedLink>
              </div>
            </>
          ))}
        <Divider />
        <div className="mb-6">
          <Label htmlFor="buffer-time" className="mb-2 text-base">
            Buffer time between your appointments:
          </Label>
          <Select
            id="buffer-time"
            className="w-fit"
            options={bufferIntervals}
            value={settings.appointmentBufferTime}
            onChange={(e) => setSettings({ ...settings, appointmentBufferTime: Number(e.target.value) })}
            defaultOption="Select time interval"
          />
        </div>
        <Divider />
        <div className="mb-6">
          <Label htmlFor="lead-time" className="mb-2 text-base">
            Appointments can be scheduled:
          </Label>
          <Select
            id="lead-time"
            className="w-fit"
            options={leadTimes}
            value={settings.appointmentLeadTime}
            onChange={(e) => setSettings({ ...settings, appointmentLeadTime: Number(e.target.value) })}
            defaultOption="Select time interval"
          />
        </div>
        <Divider />
        <div className="mb-6">
          <Label htmlFor="start-time" className="mb-2 text-base">
            Start appointment slots every:
          </Label>
          <Select
            id="start-time"
            className="w-fit"
            options={startTimeIncrements}
            value={settings.startTimeIncrement}
            onChange={(e) => setSettings({ ...settings, startTimeIncrement: Number(e.target.value) })}
            defaultOption="Select time interval"
          />
        </div>
        {cancellationPolicyEnabled && (
          <>
            <Divider />
            <div className="mb-6">
              <Label htmlFor="cancellation-policy" className="mb-2 text-base">
                Cancellation Policy:
              </Label>
              <TextArea
                id="cancellation-policy"
                className="w-full"
                rows={4}
                maxLength={250}
                placeholder="Enter your cancellation policy..."
                value={settings.cancellationPolicy || ""}
                onChange={(e) => setSettings({ ...settings, cancellationPolicy: e.target.value })}
              />
            </div>
          </>
        )}
        {automatedReviewRequestSettingsEnabled && (
          <>
            <Divider />
            <div className="mb-6">
              <Typography as="h3" variant="h5" className="mb-1">
                Review requests
              </Typography>
              <Typography variant="subtitle" className="mb-4">
                Automatically ask clients to leave you a review after their session ends.
              </Typography>
              <div className="mb-4 flex flex-row items-center">
                <Toggle
                  checked={settings.sendReviewRequestAfterPaidSession}
                  onChange={() =>
                    setSettings((prev) => ({
                      ...prev,
                      sendReviewRequestAfterPaidSession: !prev.sendReviewRequestAfterPaidSession
                    }))
                  }
                />
                <label htmlFor="send-review-after-paid-session" className="mb-0 ml-4 text-base">
                  Send a review request after a paid session
                </label>
              </div>
              <div className="flex flex-row items-center">
                <Toggle
                  checked={settings.sendReviewRequestAfterFreeConsult}
                  onChange={() =>
                    setSettings((prev) => ({
                      ...prev,
                      sendReviewRequestAfterFreeConsult: !prev.sendReviewRequestAfterFreeConsult
                    }))
                  }
                />
                <label htmlFor="send-review-after-free-consult" className="mb-0 ml-4 text-base">
                  Send a review request after a free consult
                </label>
              </div>
            </div>
          </>
        )}
      </Box>
    </div>
  )
}

export default function ProBookingSettings(props) {
  return (
    <GraphQLProvider>
      <ToastProvider>
        <BookingSettings {...props} />
      </ToastProvider>
    </GraphQLProvider>
  )
}
